import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { 
  Check, X, Clock, Calendar as CalendarIcon, User, MapPin, Phone, Mail,
  DollarSign, Loader2, Lock, CreditCard, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = { sedan: 'Coupe / Sedan', suv_small_truck: 'Small SUV / Small Truck', large_suv_truck: 'Large SUV / Van / Large Truck' };

const STATUS_STYLES = {
  pending: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', label: 'Pending Review' },
  awaiting_deposit: { bg: 'bg-blue-900/30', text: 'text-blue-400', label: 'Awaiting Deposit' },
  confirmed: { bg: 'bg-green-900/30', text: 'text-green-400', label: 'Confirmed' },
  denied: { bg: 'bg-red-900/30', text: 'text-red-400', label: 'Denied' }
};

export default function Admin() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [rescheduleModal, setRescheduleModal] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      sessionStorage.setItem('admin_logged_in', 'true');
      fetchBookings();
    } catch (error) {
      setLoginError('Invalid password');
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/bookings`);
      const sorted = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setBookings(sorted);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (bookingId, action) => {
    setActionLoading(bookingId);
    try {
      await axios.post(`${API}/admin/booking-action`, { booking_id: bookingId, action });
      fetchBookings();
    } catch (error) {
      alert(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    const session = sessionStorage.getItem('admin_logged_in');
    if (session === 'true') {
      setIsLoggedIn(true);
      fetchBookings();
    }
  }, []);

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="noise-overlay" />
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="font-heading text-2xl font-bold text-[#C9A84C]">SAC CITY SHINE</Link>
            <h1 className="font-heading text-xl text-gray-400 mt-4">Admin Login</h1>
          </div>
          
          <form onSubmit={handleLogin} className="bg-surface border border-[#262626] p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-surface-highlight border border-[#262626] flex items-center justify-center">
                <Lock className="h-8 w-8 text-white" />
              </div>
            </div>
            
            <div className="space-y-4">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full bg-transparent border-b border-[#262626] focus:border-white px-0 py-4 text-white outline-none placeholder:text-gray-600 transition-colors text-center"
                data-testid="admin-password-input"
              />
              {loginError && <p className="text-red-400 text-sm text-center">{loginError}</p>}
              <Button type="submit" className="w-full bg-white text-black hover:bg-gray-200 rounded-sm" data-testid="admin-login-btn">Login</Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="noise-overlay" />
      
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <Link to="/" className="font-heading text-xl font-bold tracking-tight text-[#C9A84C]">SAC CITY SHINE</Link>
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm">Admin Panel</span>
            <Button variant="outline" size="sm" className="border-[#262626] text-white hover:bg-surface-highlight rounded-sm"
              onClick={() => { sessionStorage.removeItem('admin_logged_in'); setIsLoggedIn(false); }} data-testid="admin-logout-btn">
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="admin-title">Bookings</h1>
            <Button variant="outline" className="border-[#262626] text-white hover:bg-surface-highlight rounded-sm" onClick={fetchBookings} disabled={loading} data-testid="refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {['pending', 'awaiting_deposit', 'confirmed', 'denied'].map(status => (
              <div key={status} className={`p-4 border border-[#262626] ${STATUS_STYLES[status].bg}`}>
                <p className={`text-2xl font-bold ${STATUS_STYLES[status].text}`}>{bookings.filter(b => b.status === status).length}</p>
                <p className="text-gray-400 text-sm">{STATUS_STYLES[status].label}</p>
              </div>
            ))}
          </div>

          {/* Bookings List */}
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-20"><p className="text-gray-400">No bookings yet</p></div>
          ) : (
            <div className="space-y-4">
              {bookings.map(booking => (
                <div key={booking.id} className="bg-surface border border-[#262626] p-6 hover:border-gray-600 transition-colors" data-testid={`booking-card-${booking.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
                            <User className="h-4 w-4" />{booking.full_name}
                          </h3>
                          <p className="text-gray-400 text-sm">{booking.service_name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.reschedule_request && (
                            <span className="px-2 py-1 text-xs font-medium bg-orange-900/30 text-orange-400">Reschedule Request</span>
                          )}
                          <span className={`px-3 py-1 text-xs font-medium ${STATUS_STYLES[booking.status]?.bg} ${STATUS_STYLES[booking.status]?.text}`}>
                            {STATUS_STYLES[booking.status]?.label}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center gap-2 text-gray-400"><CalendarIcon className="h-4 w-4" />{booking.date}</div>
                        <div className="flex items-center gap-2 text-gray-400"><Clock className="h-4 w-4" />{booking.time_slot} - {booking.end_time}</div>
                        <div className="flex items-center gap-2 text-gray-400"><Phone className="h-4 w-4" />{booking.phone}</div>
                        <div className="flex items-center gap-2 text-gray-400"><Mail className="h-4 w-4" />{booking.email}</div>
                      </div>
                      
                      <div className="flex items-start gap-2 text-gray-400 text-sm">
                        <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />{booking.service_address}
                      </div>
                      
                      <div className="text-sm">
                        <span className="text-gray-500">Vehicle:</span> <span className="text-white">{VEHICLE_TYPES[booking.vehicle_type]}</span>
                        {booking.addon_names?.length > 0 && (
                          <><span className="text-gray-500 ml-4">Add-ons:</span> <span className="text-white">{booking.addon_names.join(', ')}</span></>
                        )}
                      </div>

                      {/* Reschedule Request Info */}
                      {booking.reschedule_request && (
                        <div className="bg-orange-900/20 border border-orange-600/30 p-4 mt-4">
                          <p className="text-orange-400 font-medium flex items-center gap-2">
                            <RefreshCw className="h-4 w-4" /> Reschedule Requested
                          </p>
                          <p className="text-gray-300 text-sm mt-2">
                            <strong>New Date:</strong> {booking.reschedule_request.new_date} at {booking.reschedule_request.new_time_slot}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="lg:w-48 flex flex-col items-end gap-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white flex items-center gap-1"><DollarSign className="h-5 w-5" />{booking.total_price}</p>
                        {booking.deposit_paid && (
                          <p className="text-green-400 text-sm flex items-center gap-1 justify-end"><CreditCard className="h-3 w-3" />$100 deposit paid</p>
                        )}
                        {booking.reschedule_count > 0 && (
                          <p className="text-gray-500 text-xs mt-1">Rescheduled {booking.reschedule_count}x</p>
                        )}
                      </div>
                      
                      {/* Actions based on status */}
                      {booking.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                            onClick={() => handleAction(booking.id, 'approve')} disabled={actionLoading === booking.id} data-testid={`approve-btn-${booking.id}`}>
                            {actionLoading === booking.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" />Approve</>}
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/30 rounded-sm"
                            onClick={() => handleAction(booking.id, 'deny')} disabled={actionLoading === booking.id} data-testid={`deny-btn-${booking.id}`}>
                            <X className="h-4 w-4 mr-1" />Deny
                          </Button>
                        </div>
                      )}

                      {/* Reschedule Actions */}
                      {booking.reschedule_request && (
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-sm"
                            onClick={() => handleAction(booking.id, 'approve_reschedule')} disabled={actionLoading === booking.id}>
                            {actionLoading === booking.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" />Approve</>}
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/30 rounded-sm"
                            onClick={() => handleAction(booking.id, 'deny_reschedule')} disabled={actionLoading === booking.id}>
                            <X className="h-4 w-4 mr-1" />Deny
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
